import Accordion from "@/common/components/Accordion"
import Button from "@/common/components/Button"
import Checkbox from "@/common/components/Checkbox"
import { ImageCarousel } from "@/common/components/ImageCarousel"
import { MultipleImageUploader } from "@/common/components/MultipleImageUploader"
import SelectController from "@/common/components/SelectController"
import { UnitTextField } from "@/common/components/UnitTextField"
import { useBillingCaptureMethods } from "@/common/hooks/queries/useBillingCaptureMethods"
import useBlobs from "@/common/hooks/queries/useBlobs"
import { useProductCategories } from "@/common/hooks/queries/useProductCategories"
import useDebounce from "@/common/hooks/useDebounce"
import { BillingCaptureMethodsTransformer } from "@/common/services/billingCaptureMethodsTransformer"
import { Mapper } from "@/common/services/mapper"
import { ProductCategoryData } from "@/common/types/productCategory.type"
import { RequestedReview } from "@/features/establishmentLeadsFunnel/components/RequestedReview"
import { ReviewButtons } from "@/features/establishmentLeadsFunnel/components/ReviewButtons"
import { SendEquipmentModal } from "@/features/establishmentLeadsFunnel/components/SendEquipmentModal"
import { useCreateEstablishmentLeadContract } from "@/features/establishmentLeadsFunnel/hooks/useCreateEstablishmentLeadContract"
import useReviewSectionLead from "@/features/establishmentLeadsFunnel/hooks/useReviewSectionLead"
import { EstablishmentLeadsFunnelUpdateValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema"
import { ContractData, EstablishmentLeadData } from "@/features/establishmentLeadsFunnel/types/establishmentLead.type"
import { usePosMachines } from "@/features/pos/hooks/usePosMachines"
import { PosTransformer } from "@/features/pos/services/posTransformer"
import { useIsMutating } from "@tanstack/react-query"
import { BadgeAlertIcon, InfoIcon } from "lucide-react"
import { BaseSyntheticEvent } from "react"
import { useFormContext } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { Tooltip } from "react-tooltip"
import MaintenanceInformationSection from "./maintenanceModule/MaintenanceInformationSection"
import MaintenanceRequiredDocumentsSection from "./requiredDocumentsModule/MaintenanceRequiredDocumentsSection"

interface AccreditationInformationSectionProps {
  establishmentLeadId?: string
  tefIdentifier?: string
  establishmentLead: EstablishmentLeadData;
  contract?: ContractData
  submit?: (e?: BaseSyntheticEvent) => Promise<void>
  submittableOnBlur?: (e: React.FocusEvent<HTMLInputElement, Element>, onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void) => void
  updateLeadOnSuccess?: () => void
  redirectOnSuccess?: () => void
  disabled?: boolean
  allowUpdateRequiredDocuments?: boolean;
  allowUpdateSpecifications?: boolean;
}

export const AccreditationInformationSection = ({
  establishmentLeadId,
  establishmentLead,
  contract,
  submit,
  submittableOnBlur,
  updateLeadOnSuccess,
  redirectOnSuccess,
  disabled,
  allowUpdateRequiredDocuments,
  allowUpdateSpecifications,
}: AccreditationInformationSectionProps) => {
  const {
    accreditation_information: accreditationInformation,
    state: leadState,
    shipment,
  } = establishmentLead;

  const {
    watch,
    control,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<EstablishmentLeadsFunnelUpdateValidationSchema>();

  const navigate = useNavigate()
  const isMutating = !!useIsMutating()

  const [searchProductCategories, setSearchProductCategories] = useDebounce('')
  const [searchPos, setSearchPos] = useDebounce('')
  const [searchBillingCaptureMethods, setSearchBillingCaptureMethods] = useDebounce('')

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs()

  const { data: productCategoryOptions } = useProductCategories({
    filters: { search: searchProductCategories },
    mapper: Mapper.mapToOptions<ProductCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { data: billingCaptureMethods } = useBillingCaptureMethods({
    filters: { search: searchBillingCaptureMethods },
    mapper: Mapper.mapWithTransformer({ transformer: BillingCaptureMethodsTransformer.optionsTransformer() }),
  })

  const { data: posMachinesOptions } = usePosMachines({
    filters: {
      search: searchPos,
      status: 'in_stock'
    },
    mapper: Mapper.mapWithTransformer({ transformer: PosTransformer.optionsTransformer() }),
  })

  const {
    mutate: approveAccreditationInformationLead,
  } = useReviewSectionLead({
    establishmentLeadId,
    state: 'approved',
    section: 'accreditation_information_attributes',
    onSuccess: updateLeadOnSuccess,
  })

  const {
    mutate: declineAccreditationInformationLead,
  } = useReviewSectionLead({
    establishmentLeadId,
    state: 'disapproved',
    section: 'accreditation_information_attributes',
    onSuccess: updateLeadOnSuccess,
  })

  const {
    mutate: createEstablishmentLeadContract,
  } = useCreateEstablishmentLeadContract({
    establishmentLeadId,
    createCmContract: establishmentLead?.cm_module_enabled,
    onSuccess: redirectOnSuccess,
  })

  const canShowContract = ([
    'qualifying_quote',
    'qualifying_quote_client_review',
    'deploying',
    'deploying_management_review',
    'completed',
  ] as EstablishmentLeadData['state'][]).includes(leadState)

  const authorizerIdentifier = watch?.("accreditation_information.authorizer_identifier");

  const handlePressShowContract = () => {
    if (contract && (!establishmentLead?.cm_module_enabled || establishmentLead?.maintenance_contract)) {
      navigate(`/establishment-leads/${establishmentLeadId}/contracts/${contract?.id}/edit`);
    } else {
      createEstablishmentLeadContract();
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-[18px] font-bold">
          Dados de credenciamento
        </p>

        {canShowContract && (
          <>
            {leadState === 'qualifying_quote' ? (
              <Button
                type="button"
                name="show_contract"
                variant='yellow'
                onClick={handlePressShowContract}
                disabled={isMutating}
              >
                Gerar condição comercial
              </Button>
            ) : (
              <Button
                type="button"
                name="show_contract"
                variant="default"
                onClick={() => navigate(`/establishment-leads/${establishmentLeadId}/contracts/${contract?.id}/edit`)}
                disabled={isMutating}
              >
                Ver condição comercial
              </Button>
            )}
          </>
        )}

        {leadState === 'qualifying_review' && (
          <ReviewButtons
            name="accreditation_information"
            reviewState={accreditationInformation.last_review?.state}
            onApprove={approveAccreditationInformationLead}
            onDecline={declineAccreditationInformationLead}
            isPending={isMutating}
          />
        )}
      </div>

      {accreditationInformation?.last_review?.state === 'disapproved' && (
        <RequestedReview description={accreditationInformation.last_review?.description} />
      )}

      <div className="flex justify-between flex-wrap gap-3">
        <div className="flex flex-col gap-3">
          <p className="text-xs">
            Expectativas de atuação nos sistemas
          </p>

          <Checkbox
            label="Controle de abastecimento (CA)"
            boxClassName="size-6"
            checked={!!watch?.('ca_module_enabled')}
            onChange={(event) => {
              setValue('ca_module_enabled', event.currentTarget.checked);
              submit?.();
            }}
            disabled={disabled}
          />

          <Checkbox
            label="Controle de manutenção (CM)"
            boxClassName="size-6"
            checked={!!watch?.('cm_module_enabled')}
            onChange={(event) => {
              setValue('cm_module_enabled', event.currentTarget.checked);
              submit?.();
            }}
            disabled={disabled}
          />
        </div>

        {disabled ? (
          <>
            {accreditationInformation.photos.length ? (
              <ImageCarousel files={accreditationInformation.photos.map(photo => ({ id: photo.id, file: photo?.url }))} />
            ) : null}
          </>
        ) : (
          <MultipleImageUploader
            label={
              <>
                <p>Anexe fotos do estabelecimento</p>
                <p className="font-light text-[10px] italic">(*Ao menos uma foto deve ser da fachada.)</p>
              </>
            }
            name="equipment-images"
            accept="image/jpeg, image/png"
            uploadBlob={uploadBlob}
            submit={submit}
            control={control}
            fieldName="accreditation_information.photos"
            disabled={uploadBlobIsPending || disabled}
            errorMessage={errors?.accreditation_information?.photos?.message as string}
          />
        )}
      </div>

      {watch('ca_module_enabled') && (
        <Accordion
          defaultOpen
          head="Dados para abastecimentos (CA)"
          className="p-4"
          triggerClassName="px-0 py-1 text-[18px] font-bold"
          bodyClassName="pt-4 pb-0"
          body={(
            <div className="flex flex-col gap-4">
              <SelectController
                label="Serviços"
                name="accreditation_information.product_categories"
                control={control}
                options={productCategoryOptions}
                isMulti
                markAsRequired
                triggerOnChange={submit}
                onInputChange={value => setSearchProductCategories(value)}
                isDisabled={disabled}
                errorMessage={errors?.accreditation_information?.product_categories?.message as string}
              />
              <SelectController
                label="Meios de captura"
                labelIcon={
                  <>
                    <BadgeAlertIcon data-tooltip-id="billing-methods-info" strokeWidth={1} className="text-yellow-600" />
                    <Tooltip id="billing-methods-info">Não implementado</Tooltip>
                  </>
                }
                name="accreditation_information.capture_methods"
                control={control}
                options={billingCaptureMethods}
                onInputChange={setSearchBillingCaptureMethods}
                isMulti
                markAsRequired
                triggerOnChange={submit}
                isDisabled={disabled}
                errorMessage={errors?.accreditation_information?.capture_methods?.message as string}
              />

              {watch?.('accreditation_information.capture_methods')?.some((captureMethod) => captureMethod.perma_name === 'pos') && (
                <SelectController
                  label="POS"
                  name="accreditation_information.accreditation_information_pos_machines.machine_id"
                  control={control}
                  options={posMachinesOptions}
                  isMulti
                  markAsRequired
                  triggerOnChange={submit}
                  onInputChange={setSearchPos}
                  isDisabled={disabled}
                  errorMessage={errors?.accreditation_information?.accreditation_information_pos_machines?.machine_id?.message as string}
                />
              )}

              {authorizerIdentifier && (
                <SelectController
                  label="Identificador do TEF"
                  name="accreditation_information.authorizer_identifier"
                  control={control}
                  value={{ value: authorizerIdentifier, label: authorizerIdentifier }}
                  options={[]}
                  isMulti
                  markAsRequired
                  isDisabled={disabled}
                />
              )}

              <UnitTextField
                label="Taxa diária de juros de adiantamento"
                markAsRequired
                decimals={2}
                unit="%"
                defaultValue={watch?.('accreditation_information.payment_advance_daily_interest') || ''}
                {...register?.('accreditation_information.payment_advance_daily_interest') || {}}
                onBlur={(e) => submittableOnBlur && register && submittableOnBlur(e, register('accreditation_information.payment_advance_daily_interest').onBlur)}
                errorMessage={errors?.accreditation_information?.payment_advance_daily_interest?.message as string}
                disabled={disabled}
              />
            </div>
          )}
        />
      )}

      <MaintenanceInformationSection
        establishmentLead={establishmentLead}
        disabled={disabled}
        submitLead={submit}
        allowUpdateSpecifications={allowUpdateSpecifications}
      />

      <MaintenanceRequiredDocumentsSection
        establishmentLead={establishmentLead}
        disabled={disabled}
        allowUpdate={allowUpdateRequiredDocuments}
      />

      {(['deploying', 'deploying_management_review'] as EstablishmentLeadData['state'][]).includes(leadState) && (
        <div className="flex flex-col gap-3 bg-white-100 p-4 rounded-sm">
          <div className="flex gap-x-2 items-center">
            <InfoIcon strokeWidth={1} className="text-yellow-600" />

            <div className="flex flex-col gap-1">
              <p className="font-bold text-base">
                Envio de equipamentos e kit promocional
              </p>

              <p className="font-light text-sm italic leading-3">
                Tenha em mãos o código de rastreio ou previsão de entrega para os materiais enviados
              </p>
            </div>
          </div>

          {shipment?.status === 'pending' ? (
            <SendEquipmentModal establishmentLeadId={establishmentLeadId} />
          ) : (
            <div className="flex w-full h-10 justify-center items-center cursor-default bg-white-200 rounded px-2">
              Enviado
            </div>
          )}
        </div>
      )}
    </div>
  )
}
